import React, { useState, useMemo } from "react";
import { SimpleMarkdown } from "../../../../../../util/markdownRenderer";
import { useLocation, useNavigate } from "react-router-dom";
import "./GenericAIResponseCard.scss";
import aiChatlogo from "../../../../../../assets/Images/aiChatlogo.png";
import MailIcon from "../../../../../../assets/Icons/MailIcon";
import { AILogo } from "../../../../../../assets/Icons/SparksSolid";
import { FieldUpdateIcon } from "../../../../../../assets/Icons/InputField";
import { StatusUpdateIcon } from "../../../../../../assets/Icons/StatusUpdateIcon";
import { ThumbsUpIcon } from "../../../../../../assets/Icons/ThumbsUpIcon";
import { ThumbsDownIcon } from "../../../../../../assets/Icons/ThmbsDownIcon";
import { OrdersService } from "../../../../../../api/services/orders.service";

// Email icon component
const EmailIcon = () => (
  <svg
    width="20"
    height="16"
    viewBox="0 0 20 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 0H2C0.9 0 0 0.9 0 2V14C0 15.1 0.9 16 2 16H18C19.1 16 20 15.1 20 14V2C20 0.9 19.1 0 18 0ZM18 4L10 9L2 4V2L10 7L18 2V4Z"
      fill="#ffe600"
    />
  </svg>
);

// GenericAIResponseCard component for displaying AI responses with actions
export const GenericAIResponseCard = ({
  message,
  orderDetails,
  onActionClick,
  disableButtons = false,
  hideRecommendedActions = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState(null);
  const countryName = orderDetails?.[0]?.pagefields?.[2]?.fieldValue;

  const hideAttachFilesButton = useMemo(() => {
    return countryName?.toLowerCase() === "germany" || countryName?.toLowerCase() === "austria" || countryName?.toLowerCase() === "switzerland" || countryName?.toLowerCase().includes("saudi"); // Replace with actual condition
  }, [countryName]);

  // Check if is_static_view flag is true - show redirect message instead of AI response
  const isStaticView = message?.is_static_view === true;

  // Check if this is an actual AI response (has thread_id and turn_id) - only show feedback for AI responses
  const isAIResponse = Boolean(message?.thread_id && message?.turn_id);

  // Handler to navigate to home screen (Super Agent)
  const handleNavigateToHome = (e) => {
    e.preventDefault();
    navigate("/");
  };

  const handleFeedback = async (type) => {
    setFeedback(type);
    console.log(`[Feedback] User submitted feedback: ${type} for message:`, message);
    // Call feedback API if thread_id and turn_id are available
    const threadId = message.thread_id;
    const turnId = message.turn_id;

    //  console.log('[Feedback] Attempting to submit feedback:', { type, threadId, turnId, message });

    if (threadId && turnId) {
      try {
        await OrdersService.updateResponseFeedback({
          threadId,
          turnId,
          feedback: type === "up",
        });
        //  console.log("[Feedback] Successfully submitted feedback");
      } catch (error) {
        console.error("[Feedback] Failed to submit feedback:", error);
      }
    } else {
      console.warn(
        "[Feedback] Missing threadId or turnId, cannot submit feedback",
      );
    }
  };

  // Check if order was opened from notification
  const fromNotification = Boolean(location?.state?.fromNotification);

  // Check notification category from location state
  const notificationCategory = location?.state?.notificationCategory || "";
  const normalizedCategory = String(notificationCategory).trim().toLowerCase();

  // Define category-based visibility rules
  // Category rules when opening from notification:
  // FYI: Action Taken = NO, Email Summary = YES
  // Allocation: Action Taken = NO, Email Summary = NO, show changes array
  // Allocation failed: Action Taken = NO, Email Summary = NO, show changes array
  // Action needed: Action Taken = NO, Email Summary = YES
  // Validation Error: Action Taken = NO, Email Summary = YES
  // Priority: Action Taken = YES, Email Summary = YES
  // Axon Updates: Action Taken = YES, Email Summary = YES

  const categoryConfig = useMemo(() => {
    // console.log(
    //   "Notification category:",
    //   normalizedCategory,
    //   "From notification:",
    //   fromNotification,
    // );
    const config = {
      showActionTaken: true,
      showEmailSummary: true,
      showChangesArray: false,
      showRecommendedActions: true,
    };

    if (!fromNotification) {
      // Opening from order list - show email summary if outlook_agent, always show recommended actions
      return config;
    }

    // Opening from notification - apply category-based rules
    if (
      normalizedCategory === "fyi" ||
      normalizedCategory === "for your information"
    ) {
      config.showActionTaken = false;
      config.showEmailSummary = true;
      config.showChangesArray = false;
    } else if (normalizedCategory === "allocation") {
      config.showActionTaken = false;
      config.showEmailSummary = false;
      config.showChangesArray = true;
    } else if (
      normalizedCategory === "allocation failed" ||
      normalizedCategory === "allocationfailed"
    ) {
      config.showActionTaken = false;
      config.showEmailSummary = false;
      config.showChangesArray = true;
    } else if (
      normalizedCategory === "action needed" ||
      normalizedCategory === "actionneeded"
    ) {
      config.showActionTaken = false;
      config.showEmailSummary = true;
      config.showChangesArray = false;
    } else if (
      normalizedCategory === "validation error" ||
      normalizedCategory === "validation errors" ||
      normalizedCategory.includes("validation")
    ) {
      config.showActionTaken = false;
      config.showEmailSummary = true;
      config.showChangesArray = false;
    } else if (
      normalizedCategory === "priority" ||
      normalizedCategory === "order priority"
    ) {
      config.showActionTaken = true;
      config.showEmailSummary = true;
      config.showChangesArray = false;
    } else if (
      normalizedCategory === "axon updates" ||
      normalizedCategory === "axonupdates" ||
      normalizedCategory === "axon update"
    ) {
      config.showActionTaken = true;
      config.showEmailSummary = true;
      config.showChangesArray = false;
    } else if (
      normalizedCategory === "processing updates" ||
      normalizedCategory === "processingupdates" ||
      normalizedCategory === "processing update"
    ) {
      config.showActionTaken = false;
      config.showEmailSummary = false;
      config.showChangesArray = true;
    } else {
      // Default: hide action taken for unknown categories from notifications
      config.showActionTaken = false;
      config.showEmailSummary = true;
      config.showChangesArray = false;
    }

    return config;
  }, [fromNotification, normalizedCategory]);

  // Check for "validation error" or "validation errors" - also check if category contains "validation"
  const isValidationErrorsNotification =
    normalizedCategory === "validation errors" ||
    normalizedCategory === "validation error" ||
    normalizedCategory.includes("validation");

  const content = message.text || message.content || message.response || "";
  const isStreaming = Boolean(message.isStreaming);
  const recommendedActionsData = message.recommendedActions || {};
  // Email summaries from API - only use if present, don't retain if null
  // Sort emails by date - latest first
  const emailSummaries = useMemo(() => {
    const emails = Array.isArray(message.emailSummaries)
      ? message.emailSummaries
      : [];

    // Sort by date descending (latest first)
    return [...emails].sort((a, b) => {
      // Get date from object format or parse from string format
      const getDateValue = (emailData) => {
        if (emailData && typeof emailData === "object" && emailData.date) {
          return new Date(emailData.date).getTime();
        }
        // String format: "sender@email.com, 2026-02-07 09:30, Summary text"
        if (typeof emailData === "string") {
          const parts = emailData.split(", ");
          if (parts.length >= 2) {
            const dateStr = parts[1].trim();
            const parsed = new Date(
              dateStr.includes("T") ? dateStr : dateStr.replace(" ", "T"),
            );
            if (!isNaN(parsed.getTime())) {
              return parsed.getTime();
            }
          }
        }
        return 0;
      };

      return getDateValue(b) - getDateValue(a);
    });
  }, [message.emailSummaries]);
  const actionTakenSummary = message.actionTakenSummary || null;
  const actionTakenList = Array.isArray(message.actionTakenList)
    ? message.actionTakenList
    : [];
  const source = message.source || "openOrders";
  const isInitialOrderDetailsResponse = Boolean(
    message.isInitialOrderDetailsResponse,
  );

  // Get validation summary directly from location.state (passed from notification click)
  // Only show on the initial card (isInitialOrderDetailsResponse), not on subsequent AI responses
  const validationSummary = useMemo(() => {
    if (!isValidationErrorsNotification || !isInitialOrderDetailsResponse) {
      return [];
    }
    const changes = location?.state?.validationChanges || [];
    if (Array.isArray(changes) && changes.length > 0) {
      return changes
        .map((item) => String(item || "").trim())
        .filter((item) => item.length > 0);
    }
    return [];
  }, [
    isValidationErrorsNotification,
    isInitialOrderDetailsResponse,
    location?.state?.validationChanges,
  ]);

  const shouldShowRecommendedActions = Boolean(
    message.showRecommendedActions || isInitialOrderDetailsResponse,
  );
  const shouldSuppressRecommendedActions = Boolean(
    message.suppressRecommendedActions,
  );
  const hideStateUpdateButtons = Boolean(message.hideStateUpdateButtons);
  const shouldShowDoneAction = source === "actionNeeded";
  const currentOrderStatus = Array.isArray(orderDetails)
    ? orderDetails[0]?.headerSection?.[0]?.currentSubStage ||
      orderDetails[0]?.status ||
      ""
    : orderDetails?.headerSection?.[0]?.currentSubStage ||
      orderDetails?.status ||
      "";
  const agentType = Array.isArray(orderDetails)
    ? orderDetails[0]?.headerSection?.[0]?.agentType ||
      orderDetails[0]?.agentType ||
      ""
    : orderDetails?.headerSection?.[0]?.agentType ||
      orderDetails?.agentType ||
      "";
  const isOutlookAgent =
    String(agentType || "")
      .trim()
      .toLowerCase() === "outlook_agent";

  // For non-outlook_agent orders, hide summaries (email summary, action taken, etc.) on subsequent AI responses
  // Only the initial card should show these summaries, follow-up cards should only show recommended actions
  const hideSummariesForNonOutlook = !isInitialOrderDetailsResponse && !isOutlookAgent;

  // Extract analyzer type from order details
  const analyzerType = Array.isArray(orderDetails)
    ? orderDetails[0]?.headerSection?.[0]?.analyzer ||
      orderDetails[0]?.headerSection?.[0]?.analyzerType ||
      ""
    : orderDetails?.headerSection?.[0]?.analyzer ||
      orderDetails?.headerSection?.[0]?.analyzerType ||
      "";
  const isGeneralLedgerAnalyzer =
    String(analyzerType || "")
      .trim()
      .toLowerCase() === "general ledger analyzer";

  // Show email summary when:
  // - From notification: based on category config AND has summaries
  // - From order list: has summaries AND isOutlookAgent
  // - Hide on subsequent cards for non-outlook_agent orders
  const shouldShowEmailSummary = useMemo(() => {
    // Hide summaries on subsequent cards for non-outlook_agent orders
    if (hideSummariesForNonOutlook) return false;
    if (emailSummaries.length === 0) return false;

    if (fromNotification) {
      // From notification: use category-based config
      return categoryConfig.showEmailSummary;
    }

    // From order list: show if outlook_agent
    return isOutlookAgent;
  }, [
    hideSummariesForNonOutlook,
    emailSummaries.length,
    fromNotification,
    categoryConfig.showEmailSummary,
    isOutlookAgent,
  ]);

  // Show action taken when:
  // - From notification: based on category config AND has action taken data (no agent type check for notifications)
  // - From order list: has action taken data AND isOutlookAgent
  // - NOT for validation errors notification
  // - Hide on subsequent cards for non-outlook_agent orders
  const shouldShowActionTaken = useMemo(() => {
    // Hide summaries on subsequent cards for non-outlook_agent orders
    if (hideSummariesForNonOutlook) return false;
    if (isValidationErrorsNotification) return false;
    if (!actionTakenSummary && actionTakenList.length === 0) return false;

    if (fromNotification) {
      // From notification: use category-based config (e.g., Axon Updates, Priority allow action taken)
      // No agent type check for notifications - the category config determines visibility
      return categoryConfig.showActionTaken;
    }

    // From order list: require outlook_agent AND has data
    return isOutlookAgent;
  }, [
    hideSummariesForNonOutlook,
    isValidationErrorsNotification,
    actionTakenSummary,
    actionTakenList.length,
    fromNotification,
    categoryConfig.showActionTaken,
    isOutlookAgent,
  ]);

  // Show changes array for Allocation and Allocation failed categories
  // Hide on subsequent cards for non-outlook_agent orders
  const changesArray = useMemo(() => {
    if (hideSummariesForNonOutlook) return [];
    if (
      !fromNotification ||
      !categoryConfig.showChangesArray ||
      !isInitialOrderDetailsResponse
    ) {
      return [];
    }
    const changes = location?.state?.validationChanges || [];
    if (Array.isArray(changes) && changes.length > 0) {
      return changes
        .map((item) => String(item || "").trim())
        .filter((item) => item.length > 0);
    }
    return [];
  }, [
    hideSummariesForNonOutlook,
    fromNotification,
    categoryConfig.showChangesArray,
    isInitialOrderDetailsResponse,
    location?.state?.validationChanges,
  ]);
  const isDataReceivedStatus =
    String(currentOrderStatus || "")
      .trim()
      .toLowerCase() === "data received";
  const isDataReceivedIncompleteStatus =
    String(currentOrderStatus || "")
      .trim()
      .toLowerCase() === "data received incomplete";

  // Parse actionTakenSummary into bullet points
  const getActionTakenPoints = () => {
    if (actionTakenList.length > 0) {
      return actionTakenList
        .map((point) => String(point || "").trim())
        .filter((point) => point.length > 0);
    }
    if (!actionTakenSummary) return [];
    // Split by ". " to create separate bullet points, filter out empty strings
    return actionTakenSummary
      .split(/\.\s+/)
      .map((point) => point.trim().replace(/\.$/, "")) // Remove trailing periods
      .filter((point) => point.length > 0);
  };

  const actionTakenPoints = getActionTakenPoints();

  // Extract action buttons from the new recommendedActions format
  const getActionButtons = () => {
    // if (shouldSuppressRecommendedActions) {
    // Hide recommended actions when explicitly requested (e.g., from non-latest notification)
    if (
      shouldSuppressRecommendedActions ||
      !shouldShowRecommendedActions ||
      hideRecommendedActions
    ) {
      return [];
    }

    const doneButton = {
      id: "frontend-action-taken",
      label: "Action Taken",
      type: "generic",
    };
    const attachFileButton = {
      id: "frontend-attach-file",
      // label: "Attach File",
      label: "Attach Files",
      type: "frontendAttachFile",
    };
    // const notifyContributorsButton = {
    //   id: "frontend-notify-contributors",
    //   label: "Notify Contributors",
    //   type: "generic",
    // };

    const appendFrontendButtons = (actions) => {
      const normalizedActions = [...actions];

      const hasAttachFileAction = normalizedActions.some(
        // (item) => item.label.toLowerCase() === "attach file",
        (item) => item.label.toLowerCase() === "attach files",
      );
      // const hasNotifyContributorsAction = normalizedActions.some(
      //   (item) => item.label.toLowerCase() === "notify contributors",
      // );

      const hasDoneAction = normalizedActions.some(
        (item) => item.label.toLowerCase() === "action taken",
      );

      if (
        isDataReceivedStatus &&
        !hasAttachFileAction &&
        isGeneralLedgerAnalyzer &&
        !hideAttachFilesButton
      ) {
        normalizedActions.push(attachFileButton);
      }

      // if (isDataReceivedIncompleteStatus && !hasNotifyContributorsAction) {
      //   normalizedActions.push(notifyContributorsButton);
      // }

      if (shouldShowDoneAction && !hasDoneAction) {
        normalizedActions.push(doneButton);
      }

      return normalizedActions;
    };

    if (!recommendedActionsData) {
      return appendFrontendButtons([]);
    }

    // Backward compatibility: if actions are already transformed into button objects.
    if (Array.isArray(recommendedActionsData)) {
      const buttons = recommendedActionsData
        .map((item, index) => {
          const label =
            typeof item === "string"
              ? item
              : item?.label || item?.value || item?.name || "";

          return {
            id: item?.id || `action-${index}`,
            label: String(label || "").trim(),
            type: item?.type || "generic",
          };
        })
        .filter((item) => item.label.length > 0);

      // For openOrders: filter out fieldsUpdateRequired buttons
      if (source === "openOrders") {
        return appendFrontendButtons(
          buttons.filter((btn) => btn.type !== "fieldsUpdateRequired"),
        );
      }
      // For actionNeeded after state update: filter out stateUpdate buttons, show only fieldsUpdateRequired + Action Taken
      if (hideStateUpdateButtons) {
        return appendFrontendButtons(
          buttons.filter(
            (btn) => btn.type !== "stateUpdate" && btn.type !== "state_update",
          ),
        );
      }
      return appendFrontendButtons(buttons);
    }

    // Display recommended action buttons only when isEmpty is not explicitly true.
    if (
      typeof recommendedActionsData !== "object" ||
      // recommendedActionsData.isEmpty !== false
      recommendedActionsData.isEmpty === true
    ) {
      return appendFrontendButtons([]);
    }

    const allButtons = Object.entries(recommendedActionsData).flatMap(
      ([key, values]) => {
        if (key === "isEmpty" || !Array.isArray(values)) {
          return [];
        }

        // For openOrders: skip fieldsUpdateRequired entirely
        if (source === "openOrders" && key === "fieldsUpdateRequired") {
          return [];
        }

        // For actionNeeded after state update: skip stateUpdate buttons, show only fieldsUpdateRequired + Action Taken
        if (
          hideStateUpdateButtons &&
          (key === "stateUpdate" || key === "state_update")
        ) {
          return [];
        }

        return values
          .map((value, index) => {
            const label = String(value || "").trim();
            if (!label) {
              return null;
            }

            return {
              id: `${key}-${index}`,
              label,
              type: key,
            };
          })
          .filter(Boolean);
      },
    );

    return appendFrontendButtons(allButtons);
  };

  const actionButtons = getActionButtons();
  const hasSectionsBelowResponse =
    shouldShowEmailSummary ||
    validationSummary.length > 0 ||
    changesArray.length > 0 ||
    actionTakenPoints.length > 0 ||
    actionButtons.length > 0;

  const handleActionClick = (action) => {
    if (onActionClick) {
      onActionClick(action);
    }
  };

  // Parse email summary - handles both object format (from openOrdersSummaries) and string format (from actionRequiredLogs)
  // Object format: { sender: "Name", summary: "text", date: "2026-02-07T09:30:00Z" }
  // String format: "sender@email.com, 2026-02-07 09:30, Summary text"
  const parseEmailSummary = (emailData) => {
    // Helper function to format date consistently
    // Backend sends timestamps in UTC (e.g., "2026-06-03T07:04:34") but without the 'Z' suffix
    // We need to treat these as UTC and convert to the user's local browser timezone
    const formatDateString = (dateStr) => {
      if (!dateStr) return "";
      try {
        let normalizedDateStr = dateStr.includes("T")
          ? dateStr
          : dateStr.replace(" ", "T");

        // If the timestamp doesn't have a timezone indicator (Z or +/-offset),
        // append 'Z' to treat it as UTC so it converts correctly to local time
        if (
          !normalizedDateStr.includes("Z") &&
          !normalizedDateStr.match(/[+-]\d{2}:\d{2}$/)
        ) {
          normalizedDateStr += "Z";
        }

        const date = new Date(normalizedDateStr);
        if (!isNaN(date.getTime())) {
          return (
            date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }) +
            ", " +
            date
              .toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
              .toLowerCase()
          );
        }
      } catch (e) {
        // Keep original date string if parsing fails
      }
      return dateStr;
    };

    // If it's already an object with sender and summary, use it directly
    if (emailData && typeof emailData === "object" && emailData.sender) {
      return {
        sender: emailData.sender,
        date: formatDateString(emailData.date) || "",
        summary: emailData.summary || "",
      };
    }

    // Otherwise parse as string format
    const emailString = String(emailData || "");
    const parts = emailString.split(", ");
    if (parts.length >= 3) {
      // Format: "sender@email.com, 2026-02-07 09:30, Summary text"
      const sender = parts[0].trim();
      const dateStr = parts[1].trim();
      const summary = parts.slice(2).join(", ").trim();

      // Parse and format date
      const formattedDate = formatDateString(dateStr);

      // Extract display name from email - get first part before @ or use full sender
      let displayName = sender;
      if (sender.includes("@")) {
        const namePart = sender.split("@")[0];
        // Convert dot-separated names to readable format (e.g., "Sonia.Subhashsingh.Rajput" -> "Sonia Rajput")
        const nameParts = namePart.split(".");
        if (nameParts.length >= 2) {
          displayName = `${nameParts[0]} ${nameParts[nameParts.length - 1]}`;
        } else {
          displayName = namePart;
        }
        // Capitalize first letter of each word
        displayName = displayName.replace(/\b\w/g, (char) =>
          char.toUpperCase(),
        );
      }

      return {
        sender: displayName,
        date: formattedDate,
        summary: summary,
      };
    }
    return { sender: "Unknown", date: "", summary: emailString };
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const day = date.toLocaleDateString(undefined, {
      weekday: "long",
    });
    const time = date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${day} ${time}`.trim();
  };

  return (
    <div className={`order-chat-bubble ${message.role}`}>
      <div className="generic-ai-response-wrapper">
        {/* AI Response Card - Header with timestamp at top */}
        <div className="order-ai-card">
          <div className="order-ai-card-header">
            <div className="order-ai-card-title-logo">
              <img
                src={aiChatlogo}
                alt="LOGO"
                aria-hidden="true"
                className="order-ai-logo"
                style={{ width: 32, height: 32 }}
              />
            </div>
            <div className="order-ai-card-title-Avatar">
              <span>{message.header?.title || "GDS.ai Axon"}</span>
            </div>
            {message.timestamp && (
              <div className="order-ai-card-timestamp">
                <span>{formatTimestamp(message.timestamp)}</span>
              </div>
            )}
          </div>

          <div className="order-ai-card-body">
            {/* AI Response Text */}
            <div
              className={`order-ai-response-text markdown-content ${
                hasSectionsBelowResponse ? "" : "no-sections-below"
              }`.trim()}
            >
              {isStaticView ? (
                <span>
                  For this type of query, we recommend going to the{" "}
                  <a
                    href="/"
                    onClick={handleNavigateToHome}
                    className="super-agent-link"
                    style={{ color: "#ffe600", textDecoration: "underline", cursor: "pointer" }}
                  >
                    homepage
                  </a>{" "}
                  for a smoother experience.
                </span>
              ) : (
                <>
                  <SimpleMarkdown>{content}</SimpleMarkdown>
                  {isStreaming && <span className="streaming-cursor">▌</span>}
                </>
              )}
            </div>
            {/* Email Summary Section - displayed for action needed orders and outlook_agent orders */}
            {shouldShowEmailSummary && (
              <div className="email-summary-section-inline">
                <div className="email-summary-header">
                  <span className="email-summary-title">Summarised email</span>
                </div>
                <div className="email-summary-list">
                  {emailSummaries.map((email, index) => {
                    const parsed = parseEmailSummary(email);
                    return (
                      <div key={index} className="email-summary-card">
                        <div className="email-summary-row">
                          <div className="email-sender-info">
                            <MailIcon color="#ffe600" />
                            <span className="email-sender-name">
                              {parsed.sender}
                            </span>
                          </div>
                          <span className="email-date-time">{parsed.date}</span>
                        </div>
                        <div className="email-summary-content">
                          {parsed.summary}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Validation Summary Section - displayed for Validation Errors notifications */}
            {validationSummary.length > 0 && (
              <div className="order-ai-actions action-taken-section">
                <div className="actions-title">
                  <span className="actions-title-icon">
                    <AILogo width="18" height="18" />
                  </span>
                  <span className="actions-title-text">Validation Summary</span>
                </div>
                <ul className="action-taken-list">
                  {validationSummary.map((item, index) => (
                    <li key={index} className="action-taken-item">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Changes Array Section - displayed for Allocation and Allocation Failed notifications */}
            {changesArray.length > 0 && (
              <div className="order-ai-actions action-taken-section">
                <div className="actions-title">
                  <span className="actions-title-icon">
                    <AILogo width="18" height="18" />
                  </span>
                  <span className="actions-title-text">Summary</span>
                </div>
                <ul className="action-taken-list">
                  {changesArray.map((item, index) => (
                    <li key={index} className="action-taken-item">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Taken Section - displayed when coming from API, NOT for validation errors notifications */}
            {shouldShowActionTaken && actionTakenPoints.length > 0 && (
              <div className="order-ai-actions action-taken-section">
                <div className="actions-title">
                  <span className="actions-title-icon">
                    <AILogo width="18" height="18" />
                  </span>
                  <span className="actions-title-text">Action Taken</span>
                </div>
                <ul className="action-taken-list">
                  {actionTakenPoints.map((point, index) => (
                    <li key={index} className="action-taken-item">
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommended Actions at the bottom */}
            {actionButtons.length > 0 && (
              <div className="order-ai-actions">
                <div className="actions-title">
                  <span className="actions-title-icon">
                    <AILogo width="18" height="18" />
                  </span>
                  <span className="actions-title-text">Recommended Action</span>
                </div>
                <div className="actions-buttons-grouped">
                  {/* Field Update Section */}
                  {actionButtons.filter(
                    (btn) =>
                      btn.type === "fields_update" ||
                      btn.type === "fieldsUpdateRequired",
                  ).length > 0 && (
                    <div className="actions-category">
                      <span className="actions-category-label">
                        <FieldUpdateIcon
                          width={14}
                          height={14}
                          color="#a4a3b1"
                        />
                        Field Update
                      </span>
                      <div className="actions-buttons">
                        {actionButtons
                          .filter(
                            (btn) =>
                              btn.type === "fields_update" ||
                              btn.type === "fieldsUpdateRequired",
                          )
                          .map((action) => (
                            <button
                              key={action.id}
                              className={`order-ai-action-btn fields-update ${disableButtons ? "disabled" : ""}`.trim()}
                              onClick={() =>
                                !disableButtons && handleActionClick(action)
                              }
                              disabled={disableButtons}
                            >
                              {action.label}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Status Update Section */}
                  {actionButtons.filter(
                    (btn) =>
                      btn.type === "state_update" || btn.type === "stateUpdate",
                  ).length > 0 && (
                    <div className="actions-category">
                      <span className="actions-category-label">
                        <StatusUpdateIcon
                          width={14}
                          height={14}
                          color="#a4a3b1"
                        />
                        Status Update
                      </span>
                      <div className="actions-buttons">
                        {actionButtons
                          .filter(
                            (btn) =>
                              btn.type === "state_update" ||
                              btn.type === "stateUpdate",
                          )
                          .map((action) => (
                            <button
                              key={action.id}
                              className={`order-ai-action-btn state-update ${disableButtons ? "disabled" : ""}`.trim()}
                              onClick={() =>
                                !disableButtons && handleActionClick(action)
                              }
                              disabled={disableButtons}
                            >
                              {action.label}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Other Section */}
                  {actionButtons.filter(
                    (btn) =>
                      btn.type !== "fields_update" &&
                      btn.type !== "fieldsUpdateRequired" &&
                      btn.type !== "state_update" &&
                      btn.type !== "stateUpdate",
                  ).length > 0 && (
                    <div className="actions-category">
                      <span className="actions-category-label">Other</span>
                      <div className="actions-buttons">
                        {actionButtons
                          .filter(
                            (btn) =>
                              btn.type !== "fields_update" &&
                              btn.type !== "fieldsUpdateRequired" &&
                              btn.type !== "state_update" &&
                              btn.type !== "stateUpdate",
                          )
                          .map((action) => (
                            <button
                              key={action.id}
                              className={`order-ai-action-btn ${action.id === "frontend-action-taken" ? "action-taken-btn" : ""} ${disableButtons ? "disabled" : ""}`.trim()}
                              onClick={() =>
                                !disableButtons && handleActionClick(action)
                              }
                              disabled={disableButtons}
                            >
                              {action.label}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {isAIResponse && (
          <div className="generic-ai-response-feedback">
            <button
              className={`feedback-btn ${feedback === "up" ? "active" : ""}`}
              onClick={() => handleFeedback("up")}
              aria-label="Thumbs up"
            >
              <ThumbsUpIcon
                width={20}
                height={20}
                color={feedback === "up" ? "#1A1A24" : "#A4A3B1"}
              />
            </button>
            <button
              className={`feedback-btn ${feedback === "down" ? "active" : ""}`}
              onClick={() => handleFeedback("down")}
              aria-label="Thumbs down"
            >
              <ThumbsDownIcon
                width={20}
                height={20}
                color={feedback === "down" ? "#1A1A24" : "#A4A3B1"}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
